/**
 * Connection status presentation helpers.
 *
 * Single source of truth for how a `ConnectionStatus` is rendered across the
 * connection surfaces — the short label, the Fluent Badge appearance, the
 * semantic icon "kind", and the invalid/needs-action predicate. Previously each
 * surface (EditConnectionDialog, ConnectionDetailsPopover, ConnectionPicker,
 * CompactStatusDot, AllConnectionsPanel, …) carried its own switch, so the same
 * status could pick different colors/labels and silently drift. These helpers
 * centralize the shared decisions; surfaces keep only their own icon components
 * and style classes (which legitimately differ by context).
 *
 * Colors/appearance and icon-kind are behavior-preserving. The one label that
 * genuinely diverged — `unauthenticated`, rendered as "Needs attention" by the
 * compact surfaces vs "Unauthenticated" by the detail surfaces — is unified to
 * the canonical "Unauthenticated" everywhere.
 */
import type { CompactStatus } from '@/types/connectionStatus';
import type { ConnectionStatus } from '@/types/connectionReference';

/** Fluent Badge appearance for a connection status. */
export type ConnectionStatusAppearance =
  | 'success'
  | 'danger'
  | 'warning'
  | 'informative';

/** Semantic icon/color category for a connection status. */
export type ConnectionStatusIconKind = 'success' | 'error' | 'warning' | 'neutral';

/** Semantic color category for the compact status dot. */
export type CompactStatusColorKind = 'connected' | 'warning' | 'error' | 'none';

/**
 * Map a connection status to the reduced CompactStatus vocabulary used by the
 * compact dot indicator.
 */
export const mapToCompactStatus = (status: ConnectionStatus): CompactStatus => {
  switch (status) {
    case 'connected':
      return 'connected';
    case 'unauthenticated':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'none';
  }
};

/**
 * Canonical short label for a connection status. `unauthenticated` renders as
 * "Unauthenticated" (not the legacy compact "Needs attention").
 */
export const getConnectionStatusLabel = (status: ConnectionStatus): string => {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'error':
      return 'Error';
    case 'unauthenticated':
      return 'Unauthenticated';
    default:
      return 'Not configured';
  }
};

/**
 * Canonical short label for the reduced CompactStatus vocabulary. Keeps the
 * compact dot's existing `warning`/`unauthenticated` bucket but relabels it to
 * the canonical "Unauthenticated" so it matches the detail surfaces.
 */
export const getCompactStatusLabel = (status: CompactStatus): string => {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'error':
      return 'Error';
    case 'warning':
    case 'unauthenticated':
      return 'Unauthenticated';
    default:
      return 'Not configured';
  }
};

/**
 * Semantic color category for the compact status dot. `warning` and
 * `unauthenticated` share the warning color; anything outside the known set
 * falls back to `none`. The dot component maps this category to its own style
 * class so the color decision lives in one place.
 */
export const getCompactStatusColorKind = (status: CompactStatus): CompactStatusColorKind => {
  switch (status) {
    case 'connected':
      return 'connected';
    case 'warning':
    case 'unauthenticated':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'none';
  }
};

/**
 * Fluent Badge appearance for a connection status. Accepts `null`/`undefined`
 * (e.g. a not-yet-loaded connection) and falls back to `informative`.
 */
export const getConnectionStatusAppearance = (
  status: ConnectionStatus | null | undefined,
): ConnectionStatusAppearance => {
  switch (status) {
    case 'connected':
      return 'success';
    case 'error':
      return 'danger';
    case 'unauthenticated':
      return 'warning';
    default:
      return 'informative';
  }
};

/**
 * Semantic icon/color category for a connection status. Surfaces map this to
 * their own icon components and style classes.
 */
export const getConnectionStatusIconKind = (
  status: ConnectionStatus,
): ConnectionStatusIconKind => {
  switch (status) {
    case 'connected':
      return 'success';
    case 'error':
      return 'error';
    case 'unauthenticated':
      return 'warning';
    default:
      return 'neutral';
  }
};

/**
 * Whether a connection status represents an invalid/needs-action state
 * (error or unauthenticated).
 */
export const isConnectionInvalid = (status: ConnectionStatus): boolean =>
  status === 'error' || status === 'unauthenticated';
